/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  readonly,
  reatomArray,
  reatomAsync,
  sleep,
  withAbort,
  withAssign,
} from '@reatom/framework'

import { Array, Equivalence, Result } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth/atoms'
import { type WalletId } from '@root/entities/wallet'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import { reatomResourcePaginated } from '@root/shared/lib'
import { withEquivalence } from '@root/shared/lib/reatom/with-equivalence'

import { fetchUserTransactionRequestAction } from './transaction-requests'

import type { Network } from '@root/entities/network'
import type { PairAddress } from '@root/entities/pair'
import type { Transaction } from '@root/entities/transaction'
import type { DelugeId } from '@root/entities/user-short'
import type { PaginatedData } from '@root/shared/lib'

const LIMIT = 15

interface ReatomUserTransactionsOptions {
  network: Network.Network
  pairAddress: PairAddress | undefined
  delugeId: DelugeId | undefined
  searchDebounce?: number
  initialWalletIdsFilter?: Array<WalletId>
}

const walletIdsEquivalence = Array.makeEquivalence(
  Equivalence.strictEqual<WalletId>(),
)
export const reatomUserTransactions = ({
  network,
  pairAddress,
  delugeId,
  searchDebounce = 300,
  initialWalletIdsFilter = [],
}: ReatomUserTransactionsOptions) => {
  const searchQueryAtom = atom<string>('')
  const searchParamAtom = atom<string | undefined>(undefined)
  const typeFilterAtom = atom<Transaction.Type | undefined>(undefined)
  const statusFilterAtom = atom<Transaction.Status | undefined>(undefined)
  const walletIdsFilterInternalAtom = reatomArray<WalletId>(
    initialWalletIdsFilter,
  )
  const walletIdsFilterAtom = walletIdsFilterInternalAtom.pipe(
    withEquivalence(walletIdsEquivalence),
  )

  const transactionsAtom = reatomResourcePaginated(
    async (ctx, { limit, offset }) => {
      const currentUserId = ctx.spy(delugeIdAtom)

      const walletIds = (() => {
        const getCurrentUserWalletIds = () => {
          const filterIds = ctx.spy(walletIdsFilterAtom)
          if (filterIds.length > 0) {
            return filterIds
          }

          // all current user device wallets
          return [...ctx.spy(deviceWalletIdsAtom)]
        }

        if (!delugeId) {
          return getCurrentUserWalletIds()
        }

        if (currentUserId && currentUserId === delugeId) {
          return getCurrentUserWalletIds()
        }

        return undefined
      })()

      if (walletIds && walletIds.length === 0) {
        return Result.succeed({ data: [], total: 0 })
      }

      const type = ctx.spy(typeFilterAtom)
      const status = ctx.spy(statusFilterAtom)
      const search = ctx.spy(searchParamAtom)

      const result = await ctx.schedule(() =>
        fetchUserTransactionRequestAction(ctx, {
          network,
          limit: limit,
          offset: offset,
          walletIds,
          pairAddress,
          delugeId,
          search,
          status: status ? [status] : undefined,
          txType: type ? [type] : undefined,
        }),
      )
      return result.pipe(
        Result.map(
          (it): PaginatedData<Transaction.Transaction, 'offset'> => ({
            data: it.data,
            total: it.meta.total,
          }),
        ),
      )
    },
    {
      name: `${network}.${delugeId ?? 'user'}.paginated`,
      limit: LIMIT,
      strategy: 'offset',
      reconnectStrategy: 'reload',
    },
  )

  const setSearchAction = reatomAsync(async (ctx, search: string) => {
    searchQueryAtom(ctx, search)
    if (!search || search.length < 3) {
      searchParamAtom(ctx, undefined)
      return
    }
    await ctx.schedule(() => sleep(searchDebounce))
    searchParamAtom(ctx, search)
  }).pipe(withAbort())

  const setTypeFilterAction = action(
    (ctx, type: Transaction.Type | undefined) => {
      typeFilterAtom(ctx, type)
    },
  )

  const setStatusFilterAction = action(
    (ctx, status: Transaction.Status | undefined) => {
      statusFilterAtom(ctx, status)
    },
  )

  const setWalletIdsFilterAction = action(
    (ctx, walletIds: Array<WalletId> | undefined) => {
      walletIdsFilterInternalAtom(ctx, walletIds ? walletIds : [])
    },
  )

  return transactionsAtom.pipe(
    withAssign(() => ({
      delugeId,
      network,
      pairAddress,
      setSearch: setSearchAction,
      searchQuery: searchQueryAtom.pipe(readonly),
      typeFilter: typeFilterAtom.pipe(readonly),
      setTypeFilter: setTypeFilterAction,
      statusFilter: statusFilterAtom.pipe(readonly),
      setStatusFilter: setStatusFilterAction,
      walletIdsFilter: walletIdsFilterAtom,
      setWalletIdsFilter: setWalletIdsFilterAction,
    })),
  )
}
export type UserTransactionsModel = ReturnType<typeof reatomUserTransactions>

// TODO: Revise
// export const reatomUserTransactions = reatomFactoryCache({
//   factory: reatomUserTransactionsInternal,
//   keyFn: (options) =>
//     `${options.network}.${options.delugeId}.${options.pairAddress}`,
//   clearOnDisconnect: false,
// })
